import React from 'react';
import { Button, Spin } from 'antd';
import s from './s.module.less';
import FAQItem from './FAQItem';
import FAQModal from './FAQModal';
import ConfirmModal from './ConfirmModal';
import {
    DataManager,
    TabDataManager,
    generateTempId,
} from '../../utils/dataUpdater';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TFaqMgmt } from '../../type';

type Props = {
    dataManager: DataManager;
    onDataChange?: () => void;
};

const FAQMgmt = ({ dataManager, onDataChange }: Props) => {
    const [loading] = React.useState(false);
    const [modalVisible, setModalVisible] = React.useState(false);
    const [selectedFAQ, setSelectedFAQ] = React.useState<TFaqMgmt | undefined>(undefined);

    // 删除确认Modal状态
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [faqToDelete, setFaqToDelete] = React.useState<TFaqMgmt | undefined>(undefined);
    const [deleteLoading, setDeleteLoading] = React.useState(false);

    // 强制刷新状态 - 用于在拖拽后触发重新渲染
    const [refreshTrigger, setRefreshTrigger] = React.useState(0);

    // 为faqMgmt tab创建独立的数据管理器
    const [faqTabManager] = React.useState<TabDataManager>(() => {
        return dataManager.createTabManager('faqMgmt');
    });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    // 从tab数据管理器获取faqMgmt数据 - refreshTrigger用于强制刷新
    const faqMgmtData = React.useMemo(() => {
        return faqTabManager.getTabData('faqMgmt');
    }, [faqTabManager, refreshTrigger]);

    const handleFAQEdit = (faq: TFaqMgmt) => {
        setSelectedFAQ(faq);
        setModalVisible(true);
    };

    const handleFAQSave = async (data: TFaqMgmt) => {
        try {
            if (selectedFAQ) {
                // 编辑现有FAQ，保留原始priority
                const faqs = faqMgmtData || [];
                const index = faqs.findIndex((faq) => faq === selectedFAQ);
                if (index !== -1) {
                    // 直接更新数组，保留原始priority
                    const updatedFaqs = [...faqs];
                    updatedFaqs[index] = {
                        ...data,
                        priority: selectedFAQ.priority, // 保留原始priority
                    };
                    faqTabManager.updateTabDataDirect(updatedFaqs);

                    // 强制触发组件重新渲染
                    setRefreshTrigger((prev) => prev + 1);
                }
            } else {
                // 添加新FAQ，自动设置priority和临时ID
                const faqs = faqMgmtData || [];
                const newFAQ = {
                    ...data,
                    priority: faqs.length + 1, // 设置为当前数组长度 + 1
                    _tempId: generateTempId(), // 生成临时ID
                };
                const updatedFaqs = [...faqs, newFAQ];
                faqTabManager.updateTabDataDirect(updatedFaqs);

                // 强制触发组件重新渲染
                setRefreshTrigger((prev) => prev + 1);
            }

            // 保存到服务器
            await dataManager.saveDataToServer(
                () => {
                    setModalVisible(false);
                    setSelectedFAQ(undefined);
                    // 通知主页面数据已更新，触发预览刷新
                    onDataChange?.();
                },
                (error: any) => {
                    console.error('Failed to save FAQ:', error);
                },
            );
        } catch (error) {
            console.error('Error in handleFAQSave:', error);
        }
    };

    const handleAddFAQ = () => {
        setSelectedFAQ(undefined);
        setModalVisible(true);
    };

    // 显示删除确认Modal
    const handleDeleteClick = (faq: TFaqMgmt) => {
        setFaqToDelete(faq);
        setDeleteModalVisible(true);
    };

    // 确认删除
    const handleConfirmDelete = async () => {
        if (!faqToDelete) return;

        setDeleteLoading(true);
        try {
            const faqs = faqMgmtData || [];
            const index = faqs.findIndex((f) => f === faqToDelete);
            if (index !== -1) {
                // 直接删除数组项
                const updatedFaqs = [...faqs];
                updatedFaqs.splice(index, 1);
                faqTabManager.updateTabDataDirect(updatedFaqs);

                // 强制触发组件重新渲染
                setRefreshTrigger((prev) => prev + 1);
            }

            // 保存到服务器
            await dataManager.saveDataToServer(
                () => {
                    setDeleteModalVisible(false);
                    setFaqToDelete(undefined);
                    // 通知主页面数据已更新，触发预览刷新
                    onDataChange?.();
                },
                (error: any) => {
                    console.error('Failed to delete FAQ:', error);
                },
            );
        } catch (error) {
            console.error('Error in handleConfirmDelete:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    // 取消删除
    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setFaqToDelete(undefined);
    };

    const onFAQDragEnd = async ({ active, over }: any) => {
        if (active.id !== over?.id) {
            const faqs = faqMgmtData || [];

            // 使用_tempId进行查找，如果没有则回退到question
            const oldIndex = faqs.findIndex((faq) =>
                (faq as any)._tempId === active.id || faq.question === active.id,
            );
            const newIndex = faqs.findIndex((faq) =>
                (faq as any)._tempId === over?.id || faq.question === over?.id,
            );

            if (oldIndex !== -1 && newIndex !== -1) {
                // 更新优先级
                const updatedFaqs = [...faqs];
                const [movedItem] = updatedFaqs.splice(oldIndex, 1);
                updatedFaqs.splice(newIndex, 0, movedItem);

                // 更新所有FAQ的优先级（从1开始）

                updatedFaqs.forEach((faq, index) => {
                    faq.priority = index + 1;
                });

                // 批量更新数据
                faqTabManager.updateTabDataDirect(updatedFaqs);

                // 强制触发组件重新渲染
                setRefreshTrigger((prev) => prev + 1);

                // 通知主页面数据已更新，触发预览刷新
                onDataChange?.();

                // 保存到服务器
                await dataManager.saveDataToServer(
                    () => {
                        console.log('FAQ order updated successfully');
                    },
                    (error: any) => {
                        console.error('Failed to update FAQ order:', error);
                    },
                );
            }
        }
    };

    return (
        <div className={s.wrap}>
            <Spin spinning={loading}>
                <div className={s.header}>
                    <h3>FAQ management</h3>
                    <Button type="primary" onClick={handleAddFAQ}>
                        Add FAQ
                    </Button>
                </div>

                <DndContext sensors={sensors} onDragEnd={onFAQDragEnd}>
                    <SortableContext
                        items={faqMgmtData?.map((faq) => (faq as any)._tempId || faq.question) || []}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className={s.faqList}>
                            {faqMgmtData?.map((faq, index) => (
                                <FAQItem
                                    key={(faq as any)._tempId || faq.question}
                                    faq={faq}
                                    index={index}
                                    onEdit={() => handleFAQEdit(faq)}
                                    onRemove={() => handleDeleteClick(faq)}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </Spin>

            {/* FAQ Modal */}
            <FAQModal
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setSelectedFAQ(undefined);
                }}
                onSave={handleFAQSave}
                initialData={selectedFAQ}
            />

            {/* 删除确认Modal */}
            <ConfirmModal
                visible={deleteModalVisible}
                title="Confirm Delete FAQ"
                content={`Are you sure you want to delete FAQ "${faqToDelete?.question}"? This action cannot be undone.`}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
            />
        </div>
    );
};

export default FAQMgmt;
